import { supabase } from '../supabaseClient';
import type { TutorialKapitelId } from '../../tutorial/types';
import { istNochNichtVeroeffentlicht } from './tutorialService';

/**
 * LERNZENTRUM — Datenzugriff der Verwaltung (Plan §1.6 und §1.8, Paket E).
 *
 * Zwei Aufgaben, beide nur fuer Administratoren:
 *
 *   1. **Lueckenliste.** Die letzten Fragen aus `tutorial_fragen`. Fragen OHNE
 *      `kapitel_ids` sind die, zu denen die Hilfe nichts hergab — genau sie
 *      sind der Auftrag fuer ein neues Registerkapitel.
 *   2. **Freigabe.** `tutorial_kapitel` traegt KI-erzeugten Text. Nichts davon
 *      geht an Nutzer, solange `status` nicht `freigegeben` ist; das Umlegen
 *      ist eine Menschenentscheidung und wird mit Zeitpunkt und Person
 *      festgehalten.
 *
 * Alles fail-open: die Migration (Nr. 100/101) laeuft erst auf TEST, PROD
 * folgt nach Go des Betreibers. Bis dahin antwortet PostgREST mit
 * `42P01`/`PGRST205`, und der Reiter zeigt eine leere, ruhige Liste statt einer
 * roten Meldung. Die Rollenpruefung macht die RLS (`is_admin()`), nicht dieser
 * Client — ein Nicht-Administrator bekommt schlicht keine Zeilen.
 */

const T_FRAGEN = 'tutorial_fragen';
const T_KAPITEL = 'tutorial_kapitel';

/** So viele Fragen zeigt der Reiter. Mehr liest niemand, und es ist eine Liste, kein Archiv. */
export const FRAGEN_LIMIT = 200;

export type KapitelStatus = 'entwurf' | 'freigegeben' | 'zurueckgestellt';

export interface TutorialFrageZeile {
    id: string;
    createdAt: string | null;
    view: string | null;
    frage: string;
    antwort: string | null;
    kapitelIds: TutorialKapitelId[];
    hilfreich: boolean | null;
}

export interface TutorialKapitelZeile {
    id: TutorialKapitelId;
    titel: string;
    bereich: string;
    view: string;
    version: number;
    status: KapitelStatus;
    freigegebenAm: string | null;
    hinweisAdmin: string | null;
}

const alsText = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

const alsIdListe = (v: unknown): TutorialKapitelId[] =>
    Array.isArray(v) ? v.filter((e): e is string => typeof e === 'string' && !!e.trim()) : [];

const alsStatus = (v: unknown): KapitelStatus =>
    v === 'freigegeben' || v === 'zurueckgestellt' ? v : 'entwurf';

const aktuelleUserId = async (): Promise<string | null> => {
    try {
        const { data } = await supabase.auth.getUser();
        return data?.user?.id ?? null;
    } catch {
        return null;
    }
};

/**
 * Die letzten Fragen, neueste zuerst.
 *
 * Gefiltert wird NICHT hier, sondern in der Ansicht: die Lueckenliste ist
 * dieselbe Abfrage mit einem Haken davor, und zwei Abfragen fuer dieselben
 * Zeilen waeren eine Abfrage zuviel.
 */
export const ladeFragen = async (limit: number = FRAGEN_LIMIT): Promise<TutorialFrageZeile[]> => {
    try {
        const { data, error } = await supabase
            .from(T_FRAGEN)
            .select('id, created_at, view, frage, antwort, kapitel_ids, hilfreich')
            .order('created_at', { ascending: false })
            .limit(Math.max(1, limit));
        if (error) throw error;
        return (data ?? []).map((row: Record<string, unknown>) => ({
            id: String(row.id ?? ''),
            createdAt: alsText(row.created_at),
            view: alsText(row.view),
            frage: typeof row.frage === 'string' ? row.frage : '',
            antwort: alsText(row.antwort),
            kapitelIds: alsIdListe(row.kapitel_ids),
            hilfreich: typeof row.hilfreich === 'boolean' ? row.hilfreich : null,
        })).filter((z) => !!z.id);
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Fragen nicht geladen', e);
        return [];
    }
};

/**
 * Alle Kapitelzeilen — auch Entwuerfe und Zurueckgestelltes. Das ist der
 * Unterschied zu `ladeKapitelInhalte` im Nutzer-Service, der bewusst nur
 * `status = 'freigegeben'` liest.
 */
export const ladeKapitelVerwaltung = async (): Promise<TutorialKapitelZeile[]> => {
    try {
        const { data, error } = await supabase
            .from(T_KAPITEL)
            .select('id, titel, bereich, view, version, status, freigegeben_am, hinweis_admin')
            .order('bereich', { ascending: true })
            .order('id', { ascending: true });
        if (error) throw error;
        return (data ?? []).map((row: Record<string, unknown>) => ({
            id: String(row.id ?? ''),
            titel: typeof row.titel === 'string' ? row.titel : String(row.id ?? ''),
            bereich: typeof row.bereich === 'string' ? row.bereich : '',
            view: typeof row.view === 'string' ? row.view : '',
            version: typeof row.version === 'number' ? row.version : 1,
            status: alsStatus(row.status),
            freigegebenAm: alsText(row.freigegeben_am),
            hinweisAdmin: alsText(row.hinweis_admin),
        })).filter((z) => !!z.id);
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Kapitelliste nicht geladen', e);
        return [];
    }
};

/**
 * Freigeben oder zurueckstellen.
 *
 * `freigegeben_am` und `freigegeben_von` wandern MIT dem Status: beim
 * Freigeben werden sie gesetzt, beim Zurueckstellen geleert. Ein
 * zurueckgestelltes Kapitel mit einem Freigabestempel waere eine Aussage, die
 * nicht mehr stimmt — und der CHECK `tutorial_kapitel_freigabe_chk` in
 * Migration Nr. 100 verlangt dieselbe Paarigkeit.
 *
 * Gibt `false` zurueck, wenn nichts geschrieben wurde. Anders als beim Lesen
 * meldet die Ansicht das: eine Freigabe, die still verpufft, ist die eine
 * Stelle, an der fail-open falsch waere.
 */
export const setzeKapitelStatus = async (
    id: TutorialKapitelId,
    status: KapitelStatus,
): Promise<boolean> => {
    if (!id) return false;
    const freigabe = status === 'freigegeben';
    const userId = freigabe ? await aktuelleUserId() : null;
    try {
        const { error } = await supabase
            .from(T_KAPITEL)
            .update({
                status,
                freigegeben_am: freigabe ? new Date().toISOString() : null,
                freigegeben_von: freigabe ? userId : null,
            })
            .eq('id', id);
        if (error) throw error;
        return true;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Status nicht gesetzt', e);
        return false;
    }
};

/** Notiz der Verwaltung am Kapitel (`hinweis_admin`). Leerer Text loescht sie. */
export const speichereHinweis = async (id: TutorialKapitelId, hinweis: string): Promise<boolean> => {
    if (!id) return false;
    const wert = hinweis.trim();
    try {
        const { error } = await supabase
            .from(T_KAPITEL)
            .update({ hinweis_admin: wert ? wert.slice(0, 2000) : null })
            .eq('id', id);
        if (error) throw error;
        return true;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Hinweis nicht gespeichert', e);
        return false;
    }
};
